import { useReactiveVar } from '@apollo/client'
import { Button } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Plus } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { projectVar, selectedVersionsVar } from '../../apollo/reactive-vars'
import { useEmissions } from '../../emissions/use-emissions'
import { EmissionFragment } from '../../generated/graphql'
import { Loader } from '../common/Loader'
import { LoadingDialog } from '../common/LoadingDialog'
import { Section } from '../common/Section'
import { EmissionWithVersionId } from './carbon-footprint-tables/carbon-footprint-table-util'
import CarbonFootprintTables from './carbon-footprint-tables/CarbonFootprintTables'
import {
  getDefaultFormValues,
  mapEmissionToFormValues,
  mapFormValuesToEmissionInput,
} from './emission-util'
import EmissionDialog, { FormValues } from './EmissionDialog'

export function CarbonFootprintPage(): React.ReactElement {
  const { t } = useTranslation()
  const [isCreate, setIsCreate] = useState(true)
  const [defaultValues, setDefaultValues] = useState<Partial<FormValues>>({})
  const [dialogDisabled, setDialogDisabled] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [emissionBeingEdited, setEmissionBeingEdited] = useState<
    EmissionFragment | undefined
  >()
  const {
    createEmission,
    isCreateLoading: isCreateEmissionLoading,
    updateEmission,
    deleteEmission,
  } = useEmissions()

  const project = useReactiveVar(projectVar)
  const selectedVersions = useReactiveVar(selectedVersionsVar)

  useEffect(() => {
    if (!project) {
      return
    }
    const defaultFormValues = getDefaultFormValues(project)
    if (!defaultFormValues) {
      setDialogDisabled(true)
      return
    }
    setDefaultValues(defaultFormValues)
  }, [project])

  if (!project) {
    return <Loader />
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    setEmissionBeingEdited(undefined)
    const defaultFormValues = getDefaultFormValues(project)
    if (defaultFormValues) {
      setDefaultValues(defaultFormValues)
    }
  }

  const onEmissionAddSubmit = async (values: FormValues) => {
    const emissionInput = mapFormValuesToEmissionInput(values)
    await createEmission(emissionInput, values.buildingId)
    handleDialogClose()
  }

  const onEditEmissionSubmit = async (values: FormValues) => {
    if (!emissionBeingEdited) {
      return
    }
    const emissionInput = mapFormValuesToEmissionInput(values)
    await updateEmission(emissionBeingEdited.id, emissionInput)
    handleDialogClose()
  }

  const onDelete = async () => {
    if (!emissionBeingEdited) {
      return
    }
    await deleteEmission(emissionBeingEdited.id, emissionBeingEdited.buildingId)
    handleDialogClose()
  }

  const onCreateStart = () => {
    setIsCreate(true)
    setIsDialogOpen(true)
  }

  const onCreatePrefilledEmission = (
    emission: EmissionWithVersionId,
    newEmissionVersionId: string,
    buildingName: string,
  ) => {
    const buildingToAddEmissionTo = project.versions
      .find((version) => version.id === newEmissionVersionId)
      ?.buildings.find((b) => b.name === buildingName)

    if (buildingToAddEmissionTo) {
      setDefaultValues(
        mapEmissionToFormValues(
          emission,
          newEmissionVersionId,
          buildingToAddEmissionTo.id,
        ),
      )
      setIsCreate(true)
      setIsDialogOpen(true)
    }
  }

  const onEditStart = (emission: EmissionWithVersionId) => {
    setDefaultValues(
      mapEmissionToFormValues(
        emission,
        emission.versionId,
        emission.buildingId,
      ),
    )
    setIsCreate(false)
    setIsDialogOpen(true)
    setEmissionBeingEdited(emission)
  }

  return (
    <>
      <Section
        title={t('carbonFootprint.breakdown')}
        addon={
          <Button
            data-cy="add-emission-button"
            id="add-emission"
            variant="contained"
            color="primary"
            disabled={dialogDisabled}
            startIcon={<Plus />}
            onClick={onCreateStart}
          >
            {t('carbonFootprint.dialog.title')}
          </Button>
        }
      >
        <CarbonFootprintTables
          project={project}
          selectedVersions={selectedVersions}
          createPrefilledEmission={onCreatePrefilledEmission}
          updateEmission={onEditStart}
        />
      </Section>
      {isDialogOpen && (
        <EmissionDialog
          project={project}
          isCreate={isCreate}
          isOpen={isDialogOpen}
          onSubmit={isCreate ? onEmissionAddSubmit : onEditEmissionSubmit}
          onClose={handleDialogClose}
          onDelete={onDelete}
          defaultValues={defaultValues}
        />
      )}
      <LoadingDialog isLoading={isCreateEmissionLoading} />
    </>
  )
}
